import { useEffect, useMemo, useRef, useState } from 'react';
import type { Project } from '../../lib/types';
import { needsSchedule } from '../../lib/project';
import { HOME_PIPELINES, projectPipelineId, projectStageMeta } from '../../lib/stages';
import { Alert, Button, EmptyState, IconButton, Skeleton } from '../../ui/kit/components/primitives';
import { Icon } from '../../ui/kit/components/icons';
import { Listbox } from '../../ui/kit/components/Listbox';
import ProjectTile, { PIPELINE_ICONS } from './ProjectTile';
import Stat from '../../components/FloorStat';

type SortKey = 'recent' | 'name' | 'stage';
type ViewKey = 'grid' | 'list';

const SORT_OPTIONS = [
  { value: 'recent', label: 'Recently updated', icon: 'clock' as const },
  { value: 'name', label: 'Name A–Z', icon: 'type' as const },
  { value: 'stage', label: 'Furthest along', icon: 'layers' as const }
];

interface Props {
  projects: Project[];
  loading: boolean;
  importing?: boolean;
  error?: string;
  actionError?: string;
  onRetry: () => void;
  onStart: () => void;
  onOpen: (project: Project) => void;
  onJobs: (project: Project) => void;
  onExport: (project: Project) => void;
  onImport: (file: File) => void;
  onDelete: (project: Project) => void;
  onConvert?: (project: Project) => void;
}

const WEEK = 7 * 24 * 60 * 60 * 1000;
const PREFS_KEY = 'frameon.slate';

function readPrefs(): { sort: SortKey; view: ViewKey } {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') as { sort?: string; view?: string };
    return { sort: saved.sort === 'name' || saved.sort === 'stage' ? saved.sort : 'recent', view: saved.view === 'list' ? 'list' : 'grid' };
  } catch {
    return { sort: 'recent', view: 'grid' };
  }
}

export default function ProductionSlate({ projects, loading, importing = false, error, actionError, onRetry, onStart, onOpen, onJobs, onExport, onImport, onDelete, onConvert }: Props) {
  const searchRef = useRef<HTMLInputElement>(null);
  const archiveInput = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [undated, setUndated] = useState(false);
  const [prefs, setPrefs] = useState(readPrefs);

  const updatePrefs = (next: Partial<typeof prefs>) => setPrefs(current => {
    const merged = { ...current, ...next };
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(merged)); } catch { return merged; }
    return merged;
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"], dialog')) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      total: projects.length,
      week: projects.filter(project => now - Date.parse(project.updatedAt || '') < WEEK).length,
      shots: projects.reduce((sum, project) => sum + (project.shots?.length || 0), 0),
      undated: projects.filter(needsSchedule).length
    };
  }, [projects]);

  const kinds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const project of projects) counts.set(projectPipelineId(project), (counts.get(projectPipelineId(project)) || 0) + 1);
    return HOME_PIPELINES.filter(item => counts.has(item.id)).map(item => ({ id: item.id, title: item.title, count: counts.get(item.id) || 0 }));
  }, [projects]);

  const activeKind = kinds.some(item => item.id === kind) ? kind : 'all';

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = projects.filter(project =>
      (activeKind === 'all' || projectPipelineId(project) === activeKind) &&
      (!undated || !stats.undated || needsSchedule(project)) &&
      (!needle || [project.name, project.type, project.genre, project.logline].some(value => String(value || '').toLowerCase().includes(needle))));
    if (prefs.sort === 'name') return [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (prefs.sort === 'stage') return [...list].sort((a, b) => projectStageMeta(b).current / projectStageMeta(b).stages.length - projectStageMeta(a).current / projectStageMeta(a).stages.length);
    return list;
  }, [projects, activeKind, undated, stats.undated, query, prefs.sort]);

  const onlyUndated = undated && stats.undated > 0;
  const filtered = activeKind !== 'all' || onlyUndated || query.trim() !== '';
  const clearFilters = () => { setKind('all'); setUndated(false); setQuery(''); };
  return <section id="slate" className="studio-slate" aria-labelledby="slate-title">
    {actionError && <Alert tone="danger" title="Action failed">{actionError}</Alert>}
    {error && <Alert tone="danger" title="Could not refresh productions" action={<Button onClick={onRetry}>Retry</Button>}>{error}</Alert>}

    <div className="studio-stats studio-home-reveal" style={{ animationDelay: '60ms' }}>
      <Stat label="On the slate" value={stats.total} loading={loading} />
      <Stat label="Touched this week" value={stats.week} loading={loading} />
      <Stat label="Shots planned" value={stats.shots} loading={loading} />
      <Stat label={stats.undated === 1 ? 'Needs a delivery date' : 'Need a delivery date'} value={stats.undated} loading={loading} active={onlyUndated} onClick={stats.undated ? () => setUndated(value => !value) : undefined} />
    </div>

    <div className="studio-slate-head studio-home-reveal" style={{ animationDelay: '120ms' }}>
      <h2 id="slate-title">Productions</h2>
      <div className="studio-slate-tools">
        <label className="studio-search">
          <Icon name="search" size={15} />
          <span className="bs-sr-only">Search productions</span>
          <input ref={searchRef} className="bs-input" type="search" placeholder="Search productions" value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Escape') { setQuery(''); event.currentTarget.blur(); } }} disabled={!projects.length} />
          {!query && <kbd aria-hidden="true">/</kbd>}
        </label>
        <Listbox className="studio-sort" aria-label="Sort productions" heading="Sort productions" value={prefs.sort} onValueChange={value => updatePrefs({ sort: value as SortKey })} disabled={!projects.length} options={SORT_OPTIONS} />
        <input ref={archiveInput} className="bs-sr-only" type="file" accept=".zip,application/zip" tabIndex={-1} aria-hidden="true" onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; if (file) onImport(file); }} />
        <Button size="sm" variant="ghost" icon="download" className="studio-import" disabled={importing} aria-busy={importing || undefined} onClick={() => archiveInput.current?.click()}>{importing ? 'Importing…' : 'Import project'}</Button>
        <div className="studio-view-toggle" role="group" aria-label="Layout">
          <IconButton icon="grid" label="Grid view" variant="ghost" size="sm" aria-pressed={prefs.view === 'grid'} onClick={() => updatePrefs({ view: 'grid' })} />
          <IconButton icon="list" label="List view" variant="ghost" size="sm" aria-pressed={prefs.view === 'list'} onClick={() => updatePrefs({ view: 'list' })} />
        </div>
      </div>
    </div>

    {kinds.length > 1 && <div className="studio-filters studio-home-reveal" role="group" aria-label="Filter by production type" style={{ animationDelay: '160ms' }}>
      <button type="button" className="studio-filter" aria-pressed={activeKind === 'all'} onClick={() => setKind('all')}>All <span className="bs-mono">{projects.length}</span></button>
      {kinds.map(item => <button key={item.id} type="button" className="studio-filter" aria-pressed={activeKind === item.id} onClick={() => setKind(item.id)}><Icon name={PIPELINE_ICONS[item.id] || 'film'} size={13} />{item.title} <span className="bs-mono">{item.count}</span></button>)}
    </div>}

    <p className="bs-sr-only" role="status" aria-live="polite">{loading ? 'Loading your productions…' : filtered ? `${visible.length} of ${projects.length} productions shown` : ''}</p>

    <div className={`studio-slate-grid studio-slate-grid--${prefs.view}`} aria-busy={loading}>
      {visible.length ? visible.map((project, index) => <ProjectTile key={project.id} index={index} project={project} onOpen={() => onOpen(project)} onJobs={() => onJobs(project)} onExport={() => onExport(project)} onDelete={() => onDelete(project)} onConvert={onConvert ? () => onConvert(project) : undefined} />)
        : loading ? [0, 1, 2, 3].map(index => <div key={index} className="studio-project-card studio-project-card--skeleton" style={{ animationDelay: `${index * 60}ms` }} aria-hidden="true"><Skeleton className="studio-project-poster" height="auto" /><div className="studio-project-body"><Skeleton width="40%" height={12} /><Skeleton width="75%" height={18} /><Skeleton height={4} /></div></div>)
        : projects.length ? <div className="studio-slate-empty"><EmptyState icon="search" title="No productions match" description="Try another name, or clear the filters to see the whole slate." action={<Button onClick={clearFilters}>Clear filters</Button>} /></div>
        : <div className="studio-slate-empty"><EmptyState icon="film" title={error ? 'Productions unavailable' : 'Bring us a brief.'} description={error ? 'The studio could not read your slate. Retry to load your productions.' : 'Films, VFX shots, campaigns and product stories all start here.'} action={<Button icon={error ? 'refresh' : 'plus'} onClick={error ? onRetry : onStart}>{error ? 'Try again' : 'New production'}</Button>} /></div>}
    </div>
  </section>;
}
